import { useEffect } from 'react';

/**
 * Custom hook to dynamically manage top-ranking SEO meta tags and structured data
 * per route in a React Single Page Application (SPA).
 *
 * @param {Object} seoConfig
 * @param {string} seoConfig.title - Page title for Google SERPs
 * @param {string} seoConfig.description - High-conversion search snippet description
 * @param {string} [seoConfig.keywords] - Target commercial keywords
 * @param {string} [seoConfig.canonicalPath] - Relative route path (e.g., '/services')
 * @param {string} [seoConfig.ogType='website'] - OpenGraph type ('website' | 'article')
 * @param {string} [seoConfig.ogImage] - Custom social share image URL
 * @param {Object} [seoConfig.schema] - Custom Schema.org JSON-LD object for rich snippets
 */
export function usePageSEO({
  title,
  description,
  keywords,
  canonicalPath = '',
  ogType = 'website',
  ogImage,
  schema
}) {
  useEffect(() => {
    const baseUrl = 'https://vayonixinfotech.com';
    const currentUrl = canonicalPath ? `${baseUrl}${canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`}` : baseUrl;
    const defaultImage = `${baseUrl}/vayonix-logo-og.png`;
    const imageToUse = ogImage || defaultImage;

    // 1. Update Document Title
    if (title) {
      document.title = title;
    }

    // Helper to safely set or create a <meta> element
    const setMetaTag = (attributeName, attributeValue, content) => {
      if (!content) return;
      let element = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attributeName, attributeValue);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Helper to safely set or create a <link> element (e.g., canonical)
    const setLinkTag = (rel, href) => {
      if (!href) return;
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // 2. Standard Meta Tags
    setMetaTag('name', 'title', title);
    setMetaTag('name', 'description', description);
    if (keywords) {
      setMetaTag('name', 'keywords', keywords);
    }
    setMetaTag('name', 'robots', 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');

    // 3. Canonical URL
    setLinkTag('canonical', currentUrl);

    // 4. OpenGraph Tags
    setMetaTag('property', 'og:title', title);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:url', currentUrl);
    setMetaTag('property', 'og:type', ogType);
    setMetaTag('property', 'og:image', imageToUse);
    setMetaTag('property', 'og:site_name', 'Vayonix Infotech');

    // 5. Twitter Card Tags
    setMetaTag('name', 'twitter:title', title);
    setMetaTag('name', 'twitter:description', description);
    setMetaTag('name', 'twitter:url', currentUrl);
    setMetaTag('name', 'twitter:image', imageToUse);
    setMetaTag('name', 'twitter:card', 'summary_large_image');

    // 6. Dynamic Per-Page Schema.org JSON-LD Structured Data
    const schemaScriptId = 'vayonix-route-schema';
    let schemaScript = document.getElementById(schemaScriptId);

    if (schema) {
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.id = schemaScriptId;
        schemaScript.type = 'application/ld+json';
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = JSON.stringify(schema);
    } else if (schemaScript) {
      schemaScript.remove();
    }

    return () => {
      // Cleanup custom route schema on unmount if needed
      const existingScript = document.getElementById(schemaScriptId);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [title, description, keywords, canonicalPath, ogType, ogImage, schema]);
}

export default usePageSEO;
